import { FormikErrors, FormikTouched } from "formik"

/** An error from the server, shown until the user changes the value it was about */
export interface ServerFieldError<Values> {
  field: keyof Values
  value: unknown
  message: string
}

interface FieldErrorsForm<Values> {
  values: Values
  errors: FormikErrors<Values>
  touched: FormikTouched<Values>
}

/**
 * isInvalid and errorsFor for a formik form that also shows errors from the
 * server. Those are kept outside formik, which replaces its errors with the
 * validation schema's on every change and blur.
 */
export function getFieldErrorHelpers<Values>(
  form: FieldErrorsForm<Values>,
  serverFieldErrors: ServerFieldError<Values>[],
) {
  const serverErrorFor = (field: keyof Values) => serverFieldErrors
    .find(error => error.field === field && error.value === form.values[field])
    ?.message

  const isInvalid = (field: keyof Values) =>
    (Boolean(form.touched[field]) && form.errors[field] !== undefined)
    || serverErrorFor(field) !== undefined

  const errorsFor = (field: keyof Values): string | undefined => {
    const errors: unknown = form.errors[field]
    const schemaError = Array.isArray(errors) ? errors.join(", ") : errors
    return (typeof schemaError === "string" ? schemaError : undefined) ?? serverErrorFor(field)
  }

  return {
    isInvalid,
    errorsFor,
  }
}
